import { useRef } from "react";

import Calendar from "components/Calendar";
import FilterPopOver from "components/FilterPopOver";
import { CalenderIcon, CloseIcon, FilterIcon } from "components/icons";
import SelectMultipleTag from "components/SelectMultipleTag";

import useGetNotesFilter from "./useGetNotesFilter";
import { useTranslationContext } from "context/TranslationContext";

import { ENotesEntity, EPipelineSlug } from "@type/api/management/types";
import { TBUMemberAssignmentFilter } from "pages/BusinessUnit/ManageMemberAssignment/Model/types";
import {
  INTERVIEW_TYPE_TAG_OPTIONS,
  STAFF_TYPE_TAG_OPTIONS,
} from "../../../Model/constants";
import { DEFAULT_VALUE } from "constants/index";

interface BUMemberAssignmentFilterProps {
  selectedTab?: string;
  pipelineSlug?: EPipelineSlug;
  filter?: Partial<TBUMemberAssignmentFilter>;
  entityType?: ENotesEntity;
  onFilter?: (filter: Partial<TBUMemberAssignmentFilter>) => void;
}

const BUMemberAssignmentFilter = ({
  selectedTab,
  pipelineSlug,
  filter,
  entityType,
  onFilter,
}: BUMemberAssignmentFilterProps) => {
  const { t } = useTranslationContext();
  const filterRef = useRef<HTMLDivElement>(null);
  const shouldShowStaffAndInterviewFilters = selectedTab !== "fieldSales";

  const {
    ticketTags,
    interviewTypeTags,
    pipelineStatusTags,
    openFilter,
    isLoading,
    isEmptyFilter,
    staffTypeTags,
    pipelineStatusOptions,
    ticketListOptions,
    ticketListGlobalOptions,
    dateFilter,
    setTicketTags,
    setPipelineStatusTags,
    setInterviewTypeTags,
    setStaffTypeTags,
    onResetFilter,
    onSubmitFilter,
    onOpenFilter,
    onCloseFilter,
    onChangeDate,
    onCloseAndResetFilter,
  } = useGetNotesFilter({ filter, onFilter, selectedTab, pipelineSlug });

  const handleScrollToBottom = () => {
    if (filterRef.current) {
      filterRef.current.scrollTop = filterRef.current.scrollHeight;
    }
  };

  return (
    <div className="relative">
      {isEmptyFilter ? (
        <button
          onClick={onOpenFilter}
          className="flex justify-center items-center rounded-md border border-gray-200 h-[2.64rem] w-[2.64rem] hover:shadow-[0px_0px_1px_1px_rgb(0,122,245,0.4)] hover:ring-primary-500"
        >
          <FilterIcon width={18} height={18} />
        </button>
      ) : (
        <div className="flex gap-2 items-center justify-between border border-primary-150 bg-primary-50 text-primary-500 px-3 h-[2.64rem] rounded-lg text-base">
          <button
            className="flex items-center justify-start gap-2"
            onClick={onOpenFilter}
          >
            <FilterIcon width={16} height={16} />
            {t("filter")}
          </button>
          <button
            className="border-l border-primary-150 flex items-center justify-center pl-1"
            onClick={onCloseAndResetFilter}
          >
            <CloseIcon width={15} height={15} />
          </button>
        </div>
      )}

      {openFilter && (
        <FilterPopOver
          ref={filterRef}
          onClose={onCloseFilter}
          title={t("filter")}
          onSubmitFilter={onSubmitFilter}
          onResetFilter={onResetFilter}
          submitButtonActive={true}
          hasValue={!isEmptyFilter}
          loading={isLoading}
        >
          <Calendar
            label={t("date")}
            rightIcon={
              <div className="ml-auto">
                <CalenderIcon width={20} height={20} />
              </div>
            }
            formatDate={DEFAULT_VALUE.DATE_FORMAT}
            calendarWrapperClassName="absolute mt-[20.5rem]"
            className="h-[2.5rem]"
            containerClassName="px-1"
            labelWrapperClassName="text-gray-900"
            isSelectRange
            defaultValue={[dateFilter.start, dateFilter.end]}
            disableFutureDate
            onChange={onChangeDate}
          />

          {shouldShowStaffAndInterviewFilters && (
            <SelectMultipleTag
              values={staffTypeTags}
              setValues={setStaffTypeTags}
              label={t("staff_type")}
              options={STAFF_TYPE_TAG_OPTIONS}
              placeHolder={t("choose_staff_type")}
            />
          )}

          {shouldShowStaffAndInterviewFilters && (
            <SelectMultipleTag
              values={interviewTypeTags}
              setValues={setInterviewTypeTags}
              label={t("interview_type")}
              options={INTERVIEW_TYPE_TAG_OPTIONS}
              placeHolder={t("choose_interview_type")}
            />
          )}

          <SelectMultipleTag
            values={pipelineStatusTags}
            label={t("pipeline_status")}
            options={pipelineStatusOptions}
            placeHolder={t("choose_pipeline_status")}
            setValues={setPipelineStatusTags}
            onOptionsOpen={handleScrollToBottom}
          />

          <SelectMultipleTag
            values={ticketTags}
            label={t("ticket")}
            options={
              entityType !== ENotesEntity.BUSINESS_UNIT
                ? ticketListGlobalOptions
                : ticketListOptions
            }
            placeHolder={t("choose_ticket")}
            onOptionsOpen={handleScrollToBottom}
            setValues={setTicketTags}
          />
        </FilterPopOver>
      )}
    </div>
  );
};

export default BUMemberAssignmentFilter;
